import { logger } from "@/utils/logger";

/**
 * Client entry point for "make sure this visitor has SOME identity".
 *
 * Posts to /api/auth/anonymous, which mints a Supabase anonymous session
 * server-side and sets the HttpOnly auth cookies on the response. A browser
 * `signInAnonymously()` cannot work here: this app's auth cookies are HttpOnly,
 * so a client-minted session would be invisible to every server route.
 *
 * WHEN TO CALL IT. At the first deliberate design action, never on page load.
 * Every call that reaches an empty session creates an `auth.users` row, and
 * /create is a public, indexed, crawled page. Minting on mount would create one
 * row per bot.
 *
 * The caller is responsible for `refreshSession()` afterwards if it needs React
 * state to reflect the new session — cookies from a same-origin fetch are
 * already stored by the time this resolves, so server routes see the session
 * immediately either way.
 */

export interface EnsureAnonymousSessionResult {
  ok: boolean;
  /** True only when this call created a new anonymous user. */
  created: boolean;
  userId?: string;
}

const FAILED: EnsureAnonymousSessionResult = { ok: false, created: false };
export const ACCOUNT_UPGRADE_TIMEOUT_MS = 20_000;
export const ANONYMOUS_SESSION_TIMEOUT_MS = 8_000;

/**
 * Collapses concurrent callers onto one request. Two preset clicks in the same
 * tick would otherwise race and mint two anonymous users, the second silently
 * orphaning the first one's credit grant.
 */
let inFlight: Promise<EnsureAnonymousSessionResult> | null = null;

async function requestAnonymousSession(
  signal?: AbortSignal
): Promise<EnsureAnonymousSessionResult> {
  try {
    const response = await fetch("/api/auth/anonymous", {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: { "Content-Type": "application/json" },
      signal,
    });

    if (!response.ok) {
      logger.warn("Anonymous session request failed", {
        component: "AnonymousSession",
        status: response.status,
      });
      return FAILED;
    }

    const data = (await response.json()) as {
      success?: boolean;
      created?: boolean;
      user?: { id?: string };
    };

    if (!data?.success) {
      return FAILED;
    }

    return {
      ok: true,
      created: data.created === true,
      userId: data.user?.id,
    };
  } catch (error) {
    if (signal?.aborted) {
      return FAILED;
    }
    // Never throw: a failed mint must degrade to the old signed-out
    // behaviour, not break the click that triggered it.
    logger.warn("Anonymous session request threw", {
      component: "AnonymousSession",
      metadata: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    return FAILED;
  }
}

export function ensureAnonymousSession(): Promise<EnsureAnonymousSessionResult> {
  if (inFlight) {
    return inFlight;
  }

  // The module-level promise covers one tab. Web Locks extends the same
  // critical section across tabs, where the second request will observe the
  // cookie minted by the first instead of orphaning another anonymous user.
  const controller = new AbortController();
  const request = () => requestAnonymousSession(controller.signal);
  let lockedRequest: Promise<EnsureAnonymousSessionResult>;
  try {
    lockedRequest =
      typeof navigator !== "undefined" && navigator.locks
        ? (navigator.locks.request(
            "build-a-dress:ensure-anonymous-session",
            { signal: controller.signal },
            request
          ) as unknown as Promise<EnsureAnonymousSessionResult>)
        : request();
  } catch (error) {
    logger.warn("Anonymous session lock request threw", {
      component: "AnonymousSession",
      metadata: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    lockedRequest = request();
  }

  // A browser may expose Web Locks but reject an acquisition (for example an
  // aborted document). Preserve this API's never-throw contract and make one
  // ordinary request as the safe degradation path.
  lockedRequest = lockedRequest.catch(error => {
    if (controller.signal.aborted) {
      return FAILED;
    }
    logger.warn("Anonymous session lock request rejected", {
      component: "AnonymousSession",
      metadata: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    return request();
  });

  // Bound both lock acquisition and the underlying fetch. Besides releasing the
  // caller, this ensures the module-level `inFlight` promise cannot be poisoned
  // forever by one stalled request; a later deliberate action can retry.
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const deadline = new Promise<EnsureAnonymousSessionResult>(resolve => {
    timeoutId = setTimeout(() => {
      controller.abort();
      resolve(FAILED);
    }, ANONYMOUS_SESSION_TIMEOUT_MS);
  });
  const pendingRequest = Promise.race([lockedRequest, deadline]).finally(() => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    inFlight = null;
  });
  inFlight = pendingRequest;

  return pendingRequest;
}

/**
 * Converts the current anonymous session into a real account IN PLACE, keeping
 * the same user id — so the design, the agent session and any credits stay
 * attached. See /api/auth/upgrade.
 */
export interface UpgradeAnonymousAccountParams {
  email: string;
  password: string;
  fullName?: string;
  /** Joins the upgrade event to the design conversation that produced it. */
  agentSessionId?: string | null;
  /** Same-origin page the confirmation link should return to. */
  redirectTo?: string;
}

export type UpgradeAnonymousAccountResult =
  | { ok: true; requiresConfirmation: boolean }
  | {
      ok: false;
      /** "email_taken" is the one the customer can act on. */
      error: string;
      description?: string;
    };

export async function upgradeAnonymousAccount(
  params: UpgradeAnonymousAccountParams
): Promise<UpgradeAnonymousAccountResult> {
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => controller.abort(),
    ACCOUNT_UPGRADE_TIMEOUT_MS
  );
  try {
    const response = await fetch("/api/auth/upgrade", {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: params.email,
        password: params.password,
        full_name: params.fullName,
        agent_session_id: params.agentSessionId ?? undefined,
        redirect_to: params.redirectTo,
      }),
    });

    const data = (await response.json().catch(() => null)) as {
      success?: boolean;
      requiresConfirmation?: boolean;
      error?: string;
      error_description?: string;
    } | null;

    if (!response.ok || !data?.success) {
      return {
        ok: false,
        error: data?.error ?? "upgrade_failed",
        description: data?.error_description,
      };
    }

    return {
      ok: true,
      requiresConfirmation: data.requiresConfirmation === true,
    };
  } catch (error) {
    logger.warn("Anonymous account upgrade threw", {
      component: "AnonymousSession",
      metadata: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    return { ok: false, error: "upgrade_failed" };
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}
